import { useCallback, useState } from 'react';
import { useGoogleAccess } from './useGoogleAccess';
import { isGoogleAccessNeeded } from '../services/googleAccessService';

export interface GoogleReadGate {
  /** The levels are not known yet — show a spinner, call nothing. */
  pending: boolean;
  /** Ask for `read` (GoogleAccessPrompt) instead of listing. */
  needsRead: boolean;
  /** List the person's own Drive. */
  canList: boolean;
  /** For a list call's catch: true when it was GOOGLE_ACCESS_NEEDED (the
   *  levels were stale) — the gate then asks; false for anything else. */
  refusedBy: (err: unknown) => boolean;
  /** Back to the server's answer (on open, after a grant). */
  reset: () => void;
}

/**
 * Whether a surface may list the person's own Google Drive (Google's `read`
 * level), shared by every picker and folder chooser. `enabled` is "this
 * dialog is open on Google Drive"; while false the gate lets everything
 * through and fetches nothing. An unknown level (null) is not a "no": the
 * list call itself decides, and a 403 GOOGLE_ACCESS_NEEDED turns the gate.
 */
export function useGoogleReadGate(enabled: boolean): GoogleReadGate {
  const access = useGoogleAccess(enabled);
  const [refused, setRefused] = useState(false);
  const pending = enabled && access === null;
  const needsRead = enabled && access !== null && (access.read === false || refused);
  const refusedBy = useCallback((err: unknown) => {
    if (!isGoogleAccessNeeded(err)) return false;
    setRefused(true);
    return true;
  }, []);
  const reset = useCallback(() => setRefused(false), []);
  return { pending, needsRead, canList: !pending && !needsRead, refusedBy, reset };
}
